import * as XLSX from 'xlsx'
import i18n from '@/libs/i18n'
import store from '@/store'

export default function useExportRAT() {
  const { client } = store.state?.userStore?.userData
  const clientData = JSON.parse(localStorage.getItem("clientData") || '{}')
  const clientName = clientData?.name || client?.name || 'Cliente'

  const exportToExcel = (treatments, isTemplate = false) => {
    // Allow empty array to generate template
    if (!treatments) {
      return
    }

    // Create workbook
    const wb = XLSX.utils.book_new()

    // Add Instructivo sheet only if NOT a template
    if (!isTemplate) {
      addInstructivoSheet(wb)
    }

    // Add RAT sheet with treatments (or empty if it's a template)
    addRATSheet(wb, treatments, isTemplate)

    // Generate filename
    const today = new Date().toISOString().split('T')[0]

    // If template, use template naming
    if (isTemplate) {
      const filename = `RAT_Plantilla_${today}.xlsx`
      XLSX.writeFile(wb, filename)
    } else {
      const filename = `RAT_${today}.xlsx`
      XLSX.writeFile(wb, filename)
    }
  }

  const addInstructivoSheet = (wb) => {
    // Complete instructivo text matching v1.1 template
    const instructivoData = [
      ['PLANTILLA ARI + RAT — addval.io'],
      ['Análisis de Riesgos + Registro + Riesgo Residual + Brechas + Indicadores'],
      [],
      ['Versión 1.1 | Alineada a Ley N° 21.719 (Chile) y paridad GDPR'],
      [],
      ['1. QUÉ CONTIENE ESTA PLANTILLA'],
      ['Sistema integrado de cumplimiento en protección de datos personales alineado a Ley N° 21.719 y GDPR. Integra cinco artefactos conectados por llave común (nombre de tratamiento + ID auto-generado T-001, T-002...):'],
      [],
      ['• RAT — Registro de Actividades de Tratamiento (inventario)'],
      ['• ARI — Análisis de Riesgos Inherentes (PxI, clasificación, disparo EIPD)'],
      ['• ARR — Análisis de Riesgo Residual post-controles (P/I reducidos, decisión de tratamiento)'],
      ['• GAP — Brechas de cumplimiento Ley 21.719 (15 obligaciones precargadas)'],
      ['• Indicadores — 13 KPIs con objetivos y semaforización automática'],
      [],
      ['Además: Mapa de Riesgos dual (valores PxI + contador dinámico), Dashboard de cumplimiento, catálogo maestro protegido y extensiones del cliente.'],
      [],
      ['2. FLUJO DE TRABAJO'],
      ['Paso 1 — RAT: completa un tratamiento por fila. El ID T-001... se genera automáticamente.'],
      [],
      ['Paso 2 — RAT: marca las 3 columnas auxiliares (¿Tiene sensibles?, NNA?, biométricos?). Son la base de la lógica EIPD.'],
      [],
      ['Paso 3 — ARI: selecciona del dropdown el tratamiento en la columna TRATAMIENTO DE DATOS. El ID y los flags de sensibles/NNA/biométricos se auto-completan.'],
      [],
      ['Paso 4 — ARI: asigna score 1-5 (o N/A) a cada factor de Probabilidad, Impacto y EIPD. Al pasar el cursor sobre el header de cada factor aparece el rúbric completo con los 5 niveles descritos.'],
      [],
      ['Paso 5 — ARI: marca Sí/No en los 4 checkboxes de OBLIGATORIEDAD LEGAL (Ley 21.719). Si alguno aplica, EIPD es obligatorio por ley (sin importar scores).'],
      [],
      ['Paso 6 — ARI: revisa el cálculo automático (P, I, Riesgo, Nivel, EIPD). Si discrepas, usa EIPD OVERRIDE con justificación documentada.'],
      [],
      ['Paso 7 — ARR: describe controles preventivos y correctivos, asigna efectividad 1-5. El riesgo residual se calcula automáticamente. Decide: Aceptar/Mitigar/Transferir/Evitar, responsable, plazo.'],
      [],
      ['Paso 8 — GAP: Marca estado de cada obligación (Implementado / Parcial / No implementado / No aplica); documenta evidencia o plan de acción. Dashboard calcula % cumplimiento.'],
      [],
      ['3. METODOLOGÍA DE CÁLCULO DE RIESGO'],
      ['Escala: cada factor se evalúa 1 (Muy Bajo) a 5 (Muy Alto), o N/A si no aplica.'],
      [],
      ['Probabilidad (P) = RIESGO×(PROMEDIO de factores P que aplican, 0).'],
      ['Factores P: Periodicidad, Medios de soporte, Origen, Legitimación, Exenciones sensibles, Cantidad, Información/Transparencia, Conservación, Medios soporte cesión, Medidas encargo.'],
      [],
      ['Impacto (I) = MÁXIMO de factores I que aplican.'],
      ['Factores I: Tipo de datos, Geolocalización, Volumen, Finalidad, Proporcionalidad, Cesiones, Encargados, Transferencias.'],
      [],
      ['Riesgo inherente = P x I (sigue 1-25)'],
      ['Niveles: 1-3 Bajo | 4-8 Medio | 9-12 Alto | 15-20 Muy Alto | 25 Crítico.'],
      [],
      ['Riesgo residual (Promedio) = I reducida vs doctrinal: probabilidad en el distribuye múltiplex/aliemente, impacto se define por el poder datos.'],
      [],
      ['Riesgo EIPD = (P + I) / 2, donde:'],
      ['• F >= 8 O FAMA1_BODMOD5 >= multiplicador_presente_0))'],
      ['• F >= 8 O FAMA1_BODMOD5 >= multiplicador_presente_0))'],
      [],
      ['Multiplicadores por efectividad:'],
      ['1 Muy baja → 1,00 (sin reducción)'],
      ['2 Baja → 0,80'],
      ['3 Media → 0,50'],
      ['4 Alta → 0,20'],
      ['5 Muy alta → 0,05'],
      [],
      [],
      ['4. LÓGICA DE LA OBLIGACIÓN DE EIPD'],
      ['El EIPD es obligatorio si se cumple cualquiera de estas condiciones:'],
      [],
      ['OBLIGATORIEDAD LEGAL (Ley 21.719) — basta marcar Sí en cualquiera de los 4:'],
      ['a) Evaluación sistemática o elaboración de perfiles con datos de menores o con efectos jurídicos o económicos significativos.'],
      ['b) Tratamiento a gran escala.'],
      ['c) Tratamiento masivo a gran escala.'],
      ['d) Observación o monitoreo sistemático de zona de acceso público.'],
      ['d) Almacena o transmite datos a través de redes de telecomunicaciones de acceso público o correspondiente.'],
      [],
      ['PUNTAJE RIESGO (automática):'],
      ['• 1 Factor EIPD con score ≥ 4, o'],
      ['• 2 Factor EIPD con score ≥ 3, o'],
      ['• 3 Factor EIPD con score ≥ 2, y hay datos sensibles o NNA.'],
      [],
      ['OVERRIDE: el evaluador puede forzar Sí/No con justificación documentada.'],
      [],
      [],
      ['5. ESTRUCTURA DEL ARCHIVO (11 pestañas)'],
      ['• Instructivo — este texto.'],
      ['• RAT — registro. Columnas A-AF. 100 filas. ID auto-fill col A.'],
      ['• ARI — análisis de riesgos. Columnas A-AN. 100 filas. ID y flags Auto-pull.'],
      ['• ARR — riesgo residual post-controles. 18 columnas. Auto-pull desde ARI.'],
      ['• GAP — brechas de cumplimiento Ley 21.719 (15 obligaciones precargadas)'],
      ['• Mapa de Riesgos — dashboard con PxI y contador dinámico por zona.'],
      ['• Resultados — dashboard con 9 indicadores clave.'],
      ['• Indicadores — 13 KPIs con objetivos y semaforización.'],
      ['• SYS:catalog — tablas auxiliares por el cliente.'],
      ['• SISTEMAS — catálogo maestro (protegido, Llave addval+1).'],
      ['• Changelog — log de versiones.'],
      [],
      [],
      ['6. GLOSARIO TÉCNICO-LEGAL'],
      ['Responsable — decide fines y medios del tratamiento (Art. 2 Ley 21.719).'],
      ['Encargado — trata por cuenta del responsable.'],
      ['Corresponsable — dos o más determinan conjuntamente fines/medios (Art. 2 y 48 del reglamento).'],
      ['Corresponsable — dos o más que deciden conjuntamente fines y medios.'],
      ['Datos sensibles — raza, biométricos, identificadores, origen étnico, afiliación política/religiosa, vida u orientación sexual, genéticos.'],
      ['NNA — menores de 18 años o personas deferidas.'],
      ['EIPD — Evaluación de Impacto en Protección de Datos. Análisis previo obligatorio para tratamientos de alto riesgo (Art. 15 quater).'],
      ['Legitimación — base jurídica del tratamiento (Art. 10: consentimiento, contrato, ley, interés vital, interés público, interés legítimo). Se alinea con finalidad precisa.'],
      ['Conservación — plazo que se trata con finalidad específica.'],
      ['Transferencia internacional — transmisión fuera de Chile. Requiere garantía si no país destino no tiene nivel adecuado.'],
      ['P x I — Análisis de Riesgo Inherente (sin considerar controles).'],
      ['ARR — Análisis de Riesgo Residual (post-controles).'],
      ['GAP — Análisis de Brechas frente a 15 obligaciones legales.'],
      [],
      [],
      ['7. RÚBRICS — DÓNDE CONSULTARLAS'],
      ['Las rúbricas completas (que significan score 1 vs 5 en cada factor) aparecen al pasar el cursor sobre el header de cada columna ARI. En la hoja "ARI", en comentario de celda. De esa manera, siempre disponibles sin duplicar pesos.'],
      [],
      ['Factores con rúbric de valoración: Tipo de dato, Geolocalización, Periodicidad, Volumen, Medios de soporte, Origen, Legitimación, Exenciones sensibles, Información, Cantidad, Conservación, Medios cesión, Finalidad, Proporcionalidad, Cesiones, Medios soporte cesión, Encargados, Medidas encargo, Transferencias, Tratamiento masivo, Monitoreo zona pública.'],
      [],
      [],
      ['8. CONTACTO Y SOPORTE'],
      ['Para consultas, dudas, personalización: contactar al equipo addval.io a través del canal habitual. Esta plantilla es una herramienta de referencia y no reemplaza asesoría legal profesional.'],
      [],
      ['🖥️ Generado con addval.io — Versión 1.1']
    ]

    const ws = XLSX.utils.aoa_to_sheet(instructivoData)

    // Set column width
    ws['!cols'] = [{ wch: 130 }]

    // Set some row heights for better readability
    if (!ws['!rows']) ws['!rows'] = []
    ws['!rows'][0] = { hpt: 18 } // Title
    ws['!rows'][1] = { hpt: 14 } // Subtitle
    ws['!rows'][3] = { hpt: 14 } // Version

    XLSX.utils.book_append_sheet(wb, ws, 'Instructivo')
  }

  const addRATSheet = (wb, treatments, isTemplate = false) => {
    // Fila 1: Nombre del cliente (A1:E1) y resto combinado (F1:AF1)
    const headerRow1 = [
      clientName, // A1
      '', '', '', '', // B1-E1 (merged with A1)
      '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '' // F1-AF1 (all merged, empty for now)
    ]

    // Fila 2: Secciones principales
    const headerRow2 = [
      'IDENTIFICACIÓN DE TRATAMIENTO', // A2
      '', '', '', '', '', '', '', '', '', '', '', '', '', '', // B2-O2 (merged with A2)
      'REGISTRO DE ACTIVIDADES', // P2
      '', '', '', '', '', // Q2-U2 (merged with P2)
      'TERCEROS (CEDENTE-CESIONARIO)', // V2
      '', '', '', '', // W2-Z2 (merged with V2)
      'ENCARGADOS DE TRATAMIENTO', // AA2
      '', // AB2 (merged with AA2)
      'TRANSFERENCIAS INTERNACIONALES', // AC2
      '', // AD2 (merged with AC2)
      'MEDIDAS DE SEGURIDAD', // AE2
      '' // AF2 (merged with AE2)
    ]

    // Fila 3: Títulos individuales de cada columna
    const headerRow3 = [
      'ID', // A3
      'TRATAMIENTO DE DATOS', // B3
      'ENTIDAD', // C3
      'ROL DE LA ENTIDAD', // D3
      'DEPARTAMENTO PROPIETARIO', // E3
      'FINALIDADES DEL TRATAMIENTO', // F3
      'TIPOS DE DATOS', // G3
      '¿Tiene sensibles?', // H3
      '¿Tiene NNA?', // I3
      '¿Tiene biométricos?', // J3
      'LEGITIMACIÓN DEL TRATAMIENTO', // K3
      'DATOS SENSIBLES O CATEGORÍAS ESPECIALES', // L3
      'EXCEPCIÓN TRATAMIENTO DATOS SENSIBLES', // M3
      'PERIODICIDAD DEL TRATAMIENTO', // N3
      'ORIGEN DE LOS DATOS', // O3
      'MEDIOS DE INFORMACIÓN', // P3
      'CATEGORÍA DE INTERESADOS', // Q3
      'FORMATO DE LOS DATOS', // R3
      'ÁMBITO GEOGRÁFICO', // S3
      'VOLUMEN DE TRATAMIENTOS', // T3
      'PLAZO DE CONSERVACIÓN', // U3
      'TERCEROS FUENTES DE DATOS (CEDENTE)', // V3
      'TERCEROS FUENTES - REGULARIZACIÓN', // W3
      'TERCEROS DESTINATARIOS (CESIONARIO)', // X3
      'TERCEROS DESTINATARIOS - FINALIDAD', // Y3
      'TERCEROS DESTINATARIOS - LEGITIMACIÓN', // Z3
      'IDENTIFICACIÓN ENCARGADO / SUBENCARGADO', // AA3
      'PRESTACIÓN', // AB3
      'TRANSFERENCIA INTERNACIONAL - DESTINATARIO', // AC3
      'TRANSFERENCIA INTERNACIONAL - PAÍS', // AD3
      'RESGUARDOS SOPORTE PAPEL', // AE3
      'RESGUARDOS SOPORTE SISTEMAS' // AF3
    ]

    // Build data rows from treatments
    const dataRows = treatments.map(treatment => {
      return [
        treatment.treatmentId || '', // A: ID
        treatment.nombre || '', // B: TRATAMIENTO DE DATOS
        getEntityName(treatment), // C: ENTIDAD
        getRolText(treatment.rol), // D: ROL DE LA ENTIDAD
        treatment.departamento || '', // E: DEPARTAMENTO PROPIETARIO
        treatment.finalidades || '', // F: FINALIDADES DEL TRATAMIENTO
        formatTiposDatos(treatment.tiposDatos), // G: TIPOS DE DATOS
        treatment.hasSensitive ? 'Sí' : 'No', // H: ¿Tiene sensibles?
        treatment.hasMinors ? 'Sí' : 'No', // I: ¿Tiene NNA?
        treatment.hasBiometric ? 'Sí' : 'No', // J: ¿Tiene biométricos?
        treatment.legitimacion || '', // K: LEGITIMACIÓN DEL TRATAMIENTO
        treatment.sensiblesDescripcion || '', // L: DATOS SENSIBLES O CATEGORÍAS ESPECIALES
        treatment.excepcionSensible || '', // M: EXCEPCIÓN TRATAMIENTO DATOS SENSIBLES
        treatment.periodicidad || '', // N: PERIODICIDAD DEL TRATAMIENTO
        treatment.origen || '', // O: ORIGEN DE LOS DATOS
        treatment.medios || '', // P: MEDIOS DE INFORMACIÓN
        treatment.categoriaInteresados || '', // Q: CATEGORÍA DE INTERESADOS
        treatment.formato || '', // R: FORMATO DE LOS DATOS
        treatment.ambito || '', // S: ÁMBITO GEOGRÁFICO
        treatment.volumen || '', // T: VOLUMEN DE TRATAMIENTOS
        treatment.plazoConservacion || '', // U: PLAZO DE CONSERVACIÓN
        // Terceros
        treatment.terceros?.fuenteCedente || '', // V: TERCEROS FUENTES DE DATOS (CEDENTE)
        treatment.terceros?.fuenteRegularizacion || '', // W: TERCEROS FUENTES - REGULARIZACIÓN
        treatment.terceros?.destinatario || '', // X: TERCEROS DESTINATARIOS (CESIONARIO)
        treatment.terceros?.destinatarioFinalidad || '', // Y: TERCEROS DESTINATARIOS - FINALIDAD
        treatment.terceros?.destinatarioLegitimacion || '', // Z: TERCEROS DESTINATARIOS - LEGITIMACIÓN
        // Encargados
        treatment.encargados?.identificacion || '', // AA: IDENTIFICACIÓN ENCARGADO / SUBENCARGADO
        treatment.encargados?.prestacion || '', // AB: PRESTACIÓN
        // Transferencias
        treatment.transferencias?.destinatario || '', // AC: TRANSFERENCIA INTERNACIONAL - DESTINATARIO
        treatment.transferencias?.pais || '', // AD: TRANSFERENCIA INTERNACIONAL - PAÍS
        // Resguardos
        treatment.resguardos?.papel || '', // AE: RESGUARDOS SOPORTE PAPEL
        treatment.resguardos?.sistemas || '' // AF: RESGUARDOS SOPORTE SISTEMAS
      ]
    })

    // Create worksheet with headers (3 header rows + data)
    const wsData = [headerRow1, headerRow2, headerRow3, ...dataRows]
    const ws = XLSX.utils.aoa_to_sheet(wsData)

    // Set column widths to match template (A-AF = 32 columns)
    // Hide column A (ID) if it's a template since IDs are auto-generated
    ws['!cols'] = [
      { wch: 10, hidden: isTemplate },  // A: ID (oculto en plantilla)
      { wch: 40 },  // B: TRATAMIENTO DE DATOS
      { wch: 20 },  // C: ENTIDAD
      { wch: 18 },  // D: ROL DE LA ENTIDAD
      { wch: 25 },  // E: DEPARTAMENTO PROPIETARIO
      { wch: 50 },  // F: FINALIDADES DEL TRATAMIENTO
      { wch: 40 },  // G: TIPOS DE DATOS
      { wch: 12 },  // H: ¿Tiene sensibles?
      { wch: 12 },  // I: ¿Tiene NNA?
      { wch: 12 },  // J: ¿Tiene biométricos?
      { wch: 30 },  // K: LEGITIMACIÓN DEL TRATAMIENTO
      { wch: 40 },  // L: DATOS SENSIBLES O CATEGORÍAS ESPECIALES
      { wch: 35 },  // M: EXCEPCIÓN TRATAMIENTO DATOS SENSIBLES
      { wch: 25 },  // N: PERIODICIDAD DEL TRATAMIENTO
      { wch: 20 },  // O: ORIGEN DE LOS DATOS
      { wch: 25 },  // P: MEDIOS DE INFORMACIÓN
      { wch: 30 },  // Q: CATEGORÍA DE INTERESADOS
      { wch: 20 },  // R: FORMATO DE LOS DATOS
      { wch: 18 },  // S: ÁMBITO GEOGRÁFICO
      { wch: 20 },  // T: VOLUMEN DE TRATAMIENTOS
      { wch: 25 },  // U: PLAZO DE CONSERVACIÓN
      { wch: 35 },  // V: TERCEROS FUENTES DE DATOS (CEDENTE)
      { wch: 35 },  // W: TERCEROS FUENTES - REGULARIZACIÓN
      { wch: 35 },  // X: TERCEROS DESTINATARIOS (CESIONARIO)
      { wch: 40 },  // Y: TERCEROS DESTINATARIOS - FINALIDAD
      { wch: 35 },  // Z: TERCEROS DESTINATARIOS - LEGITIMACIÓN
      { wch: 40 },  // AA: IDENTIFICACIÓN ENCARGADO / SUBENCARGADO
      { wch: 30 },  // AB: PRESTACIÓN
      { wch: 35 },  // AC: TRANSFERENCIA INTERNACIONAL - DESTINATARIO
      { wch: 20 },  // AD: TRANSFERENCIA INTERNACIONAL - PAÍS
      { wch: 40 },  // AE: RESGUARDOS SOPORTE PAPEL
      { wch: 40 }   // AF: RESGUARDOS SOPORTE SISTEMAS
    ]

    // Add row heights
    if (!ws['!rows']) ws['!rows'] = []
    ws['!rows'][0] = { hpt: 25 } // Header row 1
    ws['!rows'][1] = { hpt: 25 } // Header row 2
    ws['!rows'][2] = { hpt: 35 } // Header row 3 (column titles)

    // Style header rows with background color
    const headerRange = XLSX.utils.decode_range(ws['!ref'])
    for (let R = 0; R <= 2; R++) {
      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: R, c: C })
        if (!ws[address]) continue
        ws[address].s = {
          fill: { fgColor: { rgb: '2F5496' } },
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } }
          }
        }
      }
    }

    // Merge cells for header groups
    if (!ws['!merges']) ws['!merges'] = []

    // Fila 1: NOMBRE CLIENTE (A1:E1)
    ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } })

    // Fila 1: Resto vacío (F1:AF1)
    ws['!merges'].push({ s: { r: 0, c: 5 }, e: { r: 0, c: 31 } })

    // Fila 2: IDENTIFICACIÓN DE TRATAMIENTO (A2:O2)
    ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: 14 } })

    // Fila 2: REGISTRO DE ACTIVIDADES (P2:U2)
    ws['!merges'].push({ s: { r: 1, c: 15 }, e: { r: 1, c: 20 } })

    // Fila 2: TERCEROS (CEDENTE-CESIONARIO) (V2:Z2)
    ws['!merges'].push({ s: { r: 1, c: 21 }, e: { r: 1, c: 25 } })

    // Fila 2: ENCARGADOS DE TRATAMIENTO (AA2:AB2)
    ws['!merges'].push({ s: { r: 1, c: 26 }, e: { r: 1, c: 27 } })

    // Fila 2: TRANSFERENCIAS INTERNACIONALES (AC2:AD2)
    ws['!merges'].push({ s: { r: 1, c: 28 }, e: { r: 1, c: 29 } })

    // Fila 2: MEDIDAS DE SEGURIDAD (AE2:AF2)
    ws['!merges'].push({ s: { r: 1, c: 30 }, e: { r: 1, c: 31 } })

    XLSX.utils.book_append_sheet(wb, ws, 'RAT')
  }

  // Helper functions
  const getRolText = (rol) => {
    if (!rol) return ''
    const rolMap = {
      responsible: 'Responsable',
      inCharge: 'Encargado',
      coResponsible: 'Corresponsable'
    }
    return rolMap[rol] || rol
  }

  const getEntityName = (treatment) => {
    // Try to get entity name from empresa_id or use client name
    return treatment.empresa?.name || client?.name || '[Entidad cliente]'
  }

  const formatTiposDatos = (tiposDatos) => {
    if (!tiposDatos || tiposDatos.length === 0) return ''
    return Array.isArray(tiposDatos) ? tiposDatos.join(', ') : tiposDatos
  }

  return {
    exportToExcel
  }
}
